import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { DOCUMENT } from '../../constants/documentTheme';
import DeveloperAccessCallout from '../../components/DeveloperAccessCallout';
import { canAccessDeveloperApiTools } from '../../utils/partnerTierGates';

const PARTNER_AUTH = { usePartnerAuth: true };

const cardSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
  p: { xs: 2.5, md: 3 },
  height: '100%',
};

const DeveloperUsage = () => {
  const { t } = useTranslation();
  const {
    entitlements: layoutEntitlements,
    setEntitlements,
    refreshEntitlements,
    canUseApiTools: layoutCanUseApiTools,
  } = useOutletContext() || {};

  const [entitlements, setLocalEntitlements] = useState(layoutEntitlements || null);
  const [usage, setUsage] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessBlocked, setAccessBlocked] = useState(false);

  const canUseApiTools = layoutCanUseApiTools ?? canAccessDeveloperApiTools(entitlements);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setAccessBlocked(false);

      if (layoutEntitlements && !canAccessDeveloperApiTools(layoutEntitlements)) {
        setLocalEntitlements(layoutEntitlements);
        setAccessBlocked(true);
        setUsage(null);
        setLogs([]);
        return;
      }

      const [usageRes, logsRes, planRes] = await Promise.all([
        api.get('/partners/developer/usage', PARTNER_AUTH),
        api.get('/partners/developer/logs?limit=50', PARTNER_AUTH).catch(() => ({ data: { logs: [] } })),
        api.get('/partners/plan', PARTNER_AUTH).catch(() => null),
      ]);
      const nextEntitlements = planRes?.data?.entitlements || layoutEntitlements || null;
      setLocalEntitlements(nextEntitlements);
      setEntitlements?.(nextEntitlements);
      setUsage(usageRes.data || null);
      setLogs(logsRes.data?.logs || []);
    } catch (err) {
      const code = err.response?.data?.error;
      const message = err.response?.data?.message || err.response?.data?.error || t('developerPortal.errors.loadFailed');
      if (code === 'partner_feature_limit' || err.response?.status === 403) {
        setAccessBlocked(true);
        if (err.response?.data?.entitlements) {
          setLocalEntitlements(err.response.data.entitlements);
          setEntitlements?.(err.response.data.entitlements);
        }
        setError(typeof message === 'string' ? message : t('developerPortal.home.noApi'));
      } else {
        setError(typeof message === 'string' ? message : t('developerPortal.errors.loadFailed'));
      }
      setUsage(null);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [t, layoutEntitlements, setEntitlements]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (layoutEntitlements) setLocalEntitlements(layoutEntitlements);
  }, [layoutEntitlements]);

  if (loading && !accessBlocked && !entitlements) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 34 }, mb: 0.75 }}>
        {t('developerPortal.usage.title')}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink2, mb: 3, maxWidth: '58ch' }}>
        {t('developerPortal.usage.subtitle')}
      </Typography>

      {(accessBlocked || !canUseApiTools) ? (
        <DeveloperAccessCallout
          entitlements={entitlements}
          errorMessage={error || undefined}
          onEntitlementsChange={async () => {
            await refreshEntitlements?.();
            await load();
          }}
        />
      ) : null}

      {error && canUseApiTools && !accessBlocked ? (
        <Alert severity="error" sx={{ mb: 2 }}>{String(error)}</Alert>
      ) : null}

      {canUseApiTools && !accessBlocked ? (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={cardSx}>
                <Typography sx={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: DOCUMENT.placeholder, mb: 1 }}>
                  {t('developerPortal.usage.sandbox')}
                </Typography>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 32 }}>
                  {usage?.sandbox?.used ?? 0}
                  <Typography component="span" sx={{ fontSize: 16, color: DOCUMENT.ink3, ml: 1 }}>
                    / {usage?.sandbox?.included ?? '—'}
                  </Typography>
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={cardSx}>
                <Typography sx={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: DOCUMENT.placeholder, mb: 1 }}>
                  {t('developerPortal.usage.production')}
                </Typography>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 32 }}>
                  {usage?.production?.used ?? 0}
                  <Typography component="span" sx={{ fontSize: 16, color: DOCUMENT.ink3, ml: 1 }}>
                    / {usage?.production?.included ?? '—'}
                  </Typography>
                </Typography>
                <Typography sx={{ mt: 1, fontSize: 13, color: DOCUMENT.ink3 }}>
                  {t('developerPortal.usage.overageHint', {
                    unit: usage?.production?.overageUnit || 10000,
                    dollars: ((usage?.production?.overageCents || 1000) / 100).toFixed(0),
                  })}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1.5 }}>
            {t('developerPortal.usage.logsTitle')}
          </Typography>
          {logs.length === 0 ? (
            <Typography sx={{ color: DOCUMENT.ink3 }}>{t('developerPortal.usage.logsEmpty')}</Typography>
          ) : (
            <Table size="small" sx={{ bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}` }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('developerPortal.usage.colTime')}</TableCell>
                  <TableCell>{t('developerPortal.usage.colMethod')}</TableCell>
                  <TableCell>{t('developerPortal.usage.colPath')}</TableCell>
                  <TableCell>{t('developerPortal.usage.colStatus')}</TableCell>
                  <TableCell>{t('developerPortal.usage.colLatency')}</TableCell>
                  <TableCell>{t('developerPortal.usage.colEnv')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id || `${log.created_at}-${log.path}`}>
                    <TableCell>{log.created_at ? new Date(log.created_at).toLocaleString() : '—'}</TableCell>
                    <TableCell>{log.method}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{log.path}</TableCell>
                    <TableCell>{log.status_code}</TableCell>
                    <TableCell>{log.latency_ms != null ? `${log.latency_ms}ms` : '—'}</TableCell>
                    <TableCell>{log.environment}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      ) : null}
    </Box>
  );
};

export default DeveloperUsage;
